import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { useStudiedGraphemesQueryOptions } from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.meaning.read";
import { queueGraphemeForLearning } from "@-/dev.improve.haniverse.lib.learn.queue";
import { useDailyNewGraphemeAllowanceQueryOptions } from "@-/dev.improve.haniverse.lib.learn.new-grapheme-limit";
import { useCreateUserCard } from "#use-create-user-card";

/**
 * A manual "Learn" for one grapheme: enrolls it while today's new-grapheme allowance has room, and
 * otherwise puts it in the learn queue, from which it is enrolled on a later day (see
 * {@link useDrainLearnQueue}). An already-studied grapheme is not new and always goes straight
 * through.
 *
 * Resolves to `"learned"` or `"queued"`. Call it sequentially for a batch: each call re-reads the
 * allowance, so a selection that straddles the limit learns what fits and queues the rest.
 */
export function useLearnOrQueueGrapheme() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const createUserCardMutation = useCreateUserCard();
  const allowanceQueryOptions = useDailyNewGraphemeAllowanceQueryOptions();
  const studiedGraphemesQueryOptions = useStudiedGraphemesQueryOptions();

  return useMutation({
    mutationFn: async (grapheme: string, { client }): Promise<"learned" | "queued"> => {
      const [allowance, studiedGraphemes] = await Promise.all([
        client.fetchQuery(allowanceQueryOptions),
        client.fetchQuery(studiedGraphemesQueryOptions),
      ]);
      if (allowance.remaining > 0 || studiedGraphemes.has(grapheme)) {
        await createUserCardMutation.mutateAsync({ graphemeCard: { front: grapheme } });
        return "learned";
      }
      await queueGraphemeForLearning({ actorDb, grapheme, now: new Date() });
      return "queued";
    },
    mutationKey: ["learnOrQueueGrapheme"],
    // Serial, so each grapheme of a batch sees the previous one's enrollment in the allowance.
    scope: { id: "learnOrQueueGrapheme" },
  });
}
